import Bugsnag from '@bugsnag/js';
import { Flex, FlexCol } from 'components/primitives/flex';
import PageHeader from 'components/auth/components/PageHeader';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { InputWrapper, StyledPassword } from 'components/auth_flow/SharedComponents';
import { FormProvider } from 'react-hook-form';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import SocialLoginButtons from 'components/auth/components/SocialLoginButtons';
import { Text } from 'components/primitives/typography';
import { useSignInWithPasswordMutation } from 'auth/mutations/authMutations';
import useIncompleteUserProcessing from 'components/auth/hooks/useIncompleteUserProcessing';
import { LoginType } from 'auth/constants';
import Input from 'components/ui/Input/Input';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import Button from 'components/ui/Button/Button';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import { useTrackEvent } from 'hooks/useAnalyticsTrack';
import Cookies from 'js-cookie';
import { useLocalStorage } from 'beautiful-react-hooks';
import boatsetterPaths from 'utils/boatsetterPaths';
import { useUserDataMutation } from '../hooks/authFlowHooks';

export default function LoginPage() {
  const router = useRouter();
  const eventIntakeTrack = useTrackEvent();
  const [galacticaSession] = useLocalStorage('galactica_session', '');
  const signIn = useSignInWithPasswordMutation();
  const userDataMutation = useUserDataMutation();
  const rudderTrack = useRudderStackTrack();
  const { handlePossiblyIncompleteAccountInAuth } = useIncompleteUserProcessing();

  const methods = useFormWithErrorTracking({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { handleSubmit } = methods;

  const submit = async (values: { password: string; email: string }) => {
    eventIntakeTrack('UserSignedInWithEmail', {
      anonymousId: Cookies.get('uuid') || '',
      sessionId: galacticaSession || '',
      appScreen: router.pathname,
    });

    try {
      await signIn.mutateAsync({
        email: values.email,
        password: values.password,
        remember_me: '1',
      });

      await handlePossiblyIncompleteAccountInAuth(LoginType.EMAIL_LOGIN, false);
    } catch (e) {
      Bugsnag.notify(e);
      console.error(e);
    }
    try {
      const userData = await userDataMutation.mutateAsync();
      rudderTrack('login_signup_success', {
        type: 'email',
        name: 'login',
        userId: userData?.id,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const { watch, register, errors } = methods;

  const phoneRef = useRef(null);

  return (
    <FormProvider {...methods}>
      <InputWrapper hidePadding>
        <form onSubmit={handleSubmit(submit)}>
          <FlexCol ref={phoneRef} gap="20px">
            <PageHeader title="Welcome back! Please log in" />
            <FlexCol>
              <Input
                placeholder="Email"
                type="email"
                hasValue={!!watch('email')}
                name="email"
                data-testid="email-input"
                hasError={!!errors?.email}
                inputRef={register({
                  required: 'Please enter email address',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i,
                    message: 'Invalid email address',
                  },
                })}
              />
              <ErrorMessage error={errors?.email} />
            </FlexCol>

            <FlexCol>
              <StyledPassword name="password" register={register} />
              <ErrorMessage error={errors?.password} />
            </FlexCol>
            <div data-testid="log-in-button">
              <Button loading={signIn.isLoading} disabled={signIn.isLoading} type="submit" text="LOG IN" fullWidth />
            </div>
          </FlexCol>
          <Flex pt="30px">
            <Link href="/a/password/new" passHref legacyBehavior>
              <Text as="a" fWeight={500} fSize="14px" rel="nofollow" data-testid="forgot-password">
                Forgot password?
              </Text>
            </Link>
          </Flex>
          <SocialLoginButtons orText="or" isSignUpMode={false} />
          <Text pt="30px" tAlign="center">
            New to Boatsetter?&nbsp;
            <Link
              href={{
                pathname: boatsetterPaths.signUp,
                query: router.query,
              }}
            >
              Sign up
            </Link>
          </Text>
        </form>
      </InputWrapper>
    </FormProvider>
  );
}
